import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  BaseFilter,
  ListResponseOfQualificationStatementType,
} from "services/KnowfullyAdminClient";
import { IdValue } from "types/common-types";

type SelectQualificationStatementTypeProps = {
  value?: string;
  onChange: (id: string | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<IdValue>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectQualificationStatementType = ({
  onChange,
  value,
  ...rest
}: SelectQualificationStatementTypeProps) => {
  const { formatMessage: t } = useIntl();
  const { qualificationService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [
    qualificationStatementType,
    setQualificationStatementType,
  ] = useState<IdValue | null>(null);

  const {
    isLoading,
    data,
  } = useQuery<ListResponseOfQualificationStatementType>(
    ["qualificationStatementTypes"],
    () =>
      qualificationService.getQualificationStatementTypes(
        new AdminListRequestOfBaseFilter()
      )
  );

  useEffect(() => {
    if (!isLoading && data?.data) {
      const typeSelected = data?.data?.find((d) => d.id === value);
      if (value && typeSelected) {
        setQualificationStatementType({
          id: typeSelected!.id,
          value: typeSelected!.name ?? "?",
        });
      }
    }
  }, [isLoading, data, value]);

  const fetchQualificationStatementTypes = async (searchTerm: string) => {
    const result = await queryClient.fetchQuery<ListResponseOfQualificationStatementType>(
      ["qualificationStatementTypes", searchTerm],
      () =>
        qualificationService.getQualificationStatementTypes(
          new AdminListRequestOfBaseFilter({
            pageNumber: 0,
            pageSize: 20,
            sortField: "name",
            parameters: BaseFilter.fromJS({
              searchText: searchTerm === "" ? undefined : searchTerm,
            }),
          })
        )
    );

    const qualificationStatementTypes = {
      ...result,
      data: (result.data ?? []).map(({ id, name }) => ({
        id,
        value: name ?? "?",
      })),
    };

    return { result: qualificationStatementTypes, forSearchTerm: searchTerm };
  };

  const onChangeQualification = (qualificationStatement: IdValue | null) => {
    setQualificationStatementType(qualificationStatement);
    onChange(qualificationStatement?.id ?? null);
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={qualificationStatementType}
      isLoadingInitialValue={isLoading}
      labelField="value"
      idField="id"
      fetchData={fetchQualificationStatementTypes}
      failMessage={t({ id: "fetchError" })}
      onChange={onChangeQualification}
    />
  );
};

export default SelectQualificationStatementType;
